import { useState } from 'react';
import {
  useMembers,
  useUpdateMemberRole,
  useRemoveMember,
} from '../../api/hooks/admin.hook';
import type { OrgMember } from '../../api/services/admin';

export const MembersPage = () => {
  const { data, isLoading } = useMembers();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();

  const members = data?.members ?? [];

  if (isLoading) {
    return <div className="text-sm text-neutral-400">Loading members...</div>;
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-neutral-900">Members</h1>
        <span className="text-xs text-neutral-400">{members.length} total</span>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-100 text-left text-xs text-neutral-500 uppercase tracking-wide">
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Joined</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <MemberRow
                key={member.user_id}
                member={member}
                onRoleChange={(role) =>
                  updateRole.mutate({ userId: member.user_id, role })
                }
                onRemove={() => {
                  if (
                    window.confirm(
                      `Remove ${member.email} from the organization? Their login will be disabled.`,
                    )
                  ) {
                    removeMember.mutate(member.user_id);
                  }
                }}
                isUpdating={updateRole.isLoading || removeMember.isLoading}
              />
            ))}
          </tbody>
        </table>

        {members.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-neutral-400">
            No members yet.
          </div>
        )}
      </div>
    </div>
  );
};

const MemberRow = ({
  member,
  onRoleChange,
  onRemove,
  isUpdating,
}: {
  member: OrgMember;
  onRoleChange: (role: string) => void;
  onRemove: () => void;
  isUpdating: boolean;
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <tr className="border-b border-neutral-50 hover:bg-neutral-50/50">
      <td className="px-4 py-2.5">
        <div className="font-medium text-neutral-900">{member.email}</div>
        {member.last_active_at && (
          <div className="text-[10px] text-neutral-400 mt-0.5">
            Active {new Date(member.last_active_at).toLocaleDateString()}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        <select
          value={member.org_role}
          onChange={(e) => onRoleChange(e.target.value)}
          disabled={isUpdating}
          className="text-xs bg-neutral-50 border border-neutral-200 rounded px-2 py-1 text-neutral-700 disabled:opacity-50"
        >
          <option value="admin">Admin</option>
          <option value="member">Member</option>
        </select>
      </td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${
            member.status === 'active'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-600'
          }`}
        >
          {member.status}
        </span>
      </td>
      <td className="px-4 py-2.5 text-neutral-500 text-xs">
        {new Date(member.joined_at).toLocaleDateString()}
      </td>
      <td className="px-4 py-2.5 text-right relative">
        <button
          type="button"
          onClick={() => setShowMenu(!showMenu)}
          className="text-neutral-400 hover:text-neutral-600 px-2 py-1 text-xs"
        >
          ...
        </button>
        {showMenu && (
          <div className="absolute right-4 top-full mt-1 bg-white border border-neutral-200 rounded shadow-lg z-10 py-1 min-w-[120px]">
            <button
              type="button"
              onClick={() => {
                onRemove();
                setShowMenu(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
            >
              Remove member
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};
